import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import MatchPreviewPanel from "@/components/MatchPreviewPanel";
import {
  ArrowLeft, Buildings, MapPin, Link as LinkIcon, ShareNetwork, Calendar,
  ArrowsCounterClockwise, FileText, PencilSimpleLine, Check, Warning,
} from "@phosphor-icons/react";

const STATUS_TONE = {
  Active: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Expired: "bg-amber-50 text-amber-800 border-amber-200",
  Closed: "bg-slate-100 text-slate-600 border-slate-200",
  Unavailable: "bg-red-50 text-red-800 border-red-200",
  Archived: "bg-slate-100 text-slate-500 border-slate-200",
};

const fmt = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return d; }
};

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [attachMode, setAttachMode] = useState(false);
  const [manualJd, setManualJd] = useState("");
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setBusy(true); setLoadError(null);
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
    } catch (ex) {
      const code = ex?.response?.status;
      setLoadError({ kind: code === 404 ? "not_found" : code === 403 ? "forbidden" : "error",
                     msg: formatApiError(ex.response?.data?.detail) || "Failed to load job." });
    } finally { setBusy(false); }
    try {
      const { data } = await api.get(`/jobs/${id}/jd`);
      setSnapshot(data.snapshot);
    } catch { setSnapshot(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const refresh = async () => {
    setRefreshBusy(true);
    try {
      const { data } = await api.post(`/jobs/${id}/refresh`);
      toast.success(`Job status: ${data.job_status}${data.jd_changed ? " (JD updated)" : ""}`);
      await load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Refresh failed");
    } finally { setRefreshBusy(false); }
  };

  const attachManual = async () => {
    if (manualJd.trim().length < 20) {
      toast.error("Paste at least 20 characters of the JD.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/attach-jd`, { jd_text: manualJd });
      toast.success("JD snapshot attached");
      setAttachMode(false); setManualJd("");
      await load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Attach failed");
    } finally { setBusy(false); }
  };

  if (loadError) {
    return (
      <div className="space-y-4" data-testid="job-detail-error">
        <Link to="/jobs" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-slate-800">
          <ArrowLeft size={14} /> Back to Job Library
        </Link>
        <div className={`border rounded-lg p-6 ${loadError.kind === "forbidden"
            ? "bg-amber-50 border-amber-200 text-amber-800"
            : loadError.kind === "not_found"
            ? "bg-slate-50 border-slate-200 text-slate-700"
            : "bg-red-50 border-red-200 text-red-800"}`}
             data-testid={`job-detail-error-${loadError.kind}`}>
          <div className="text-base font-semibold mb-1">
            {loadError.kind === "forbidden" ? "Access denied"
              : loadError.kind === "not_found" ? "Job not found" : "Unable to load job"}
          </div>
          <div className="text-sm">{loadError.msg}</div>
        </div>
      </div>
    );
  }
  if (!job) return <div className="text-slate-500 text-sm" data-testid="job-detail-loading">Loading job…</div>;

  const tone = STATUS_TONE[job.job_status] || STATUS_TONE.Active;

  return (
    <div className="space-y-5" data-testid="job-detail-page">
      <Link to="/jobs" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-slate-800">
        <ArrowLeft size={14} /> Back to Job Library
      </Link>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight"
                  data-testid="job-detail-title">{job.job_title || "Untitled"}</h1>
              <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${tone}`}
                    data-testid="job-detail-status">{job.job_status || "Active"}</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-slate-600">
              <span className="inline-flex items-center gap-1"><Buildings size={12} /> {job.company || "—"}</span>
              <span className="inline-flex items-center gap-1"><MapPin size={12} /> {job.location || "—"}</span>
              {job.work_arrangement && <span>· {job.work_arrangement}</span>}
              {job.employment_type && <span>· {job.employment_type}</span>}
            </div>
            {(job.reuse_count || 0) > 0 && (
              <div className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-700"
                   data-testid="job-detail-reuse">
                <ShareNetwork size={12} /> Reused {job.reuse_count}× by other executives
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {job.job_url && (
              <a href={job.job_url} target="_blank" rel="noreferrer"
                 data-testid="job-detail-external-link"
                 className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">
                <LinkIcon size={12} /> Open source
              </a>
            )}
            <button onClick={refresh} disabled={refreshBusy || !job.job_url}
                    data-testid="job-detail-refresh"
                    className="text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
              <ArrowsCounterClockwise size={12} /> {refreshBusy ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left — JD snapshot */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-800 inline-flex items-center gap-1">
              <FileText size={14} /> Job Description (immutable snapshot)
            </div>
            {snapshot && (
              <div className="text-[11px] text-slate-500">
                Captured {fmt(snapshot.captured_at)} · {snapshot.source}
              </div>
            )}
          </div>

          {snapshot ? (
            <div className="text-sm text-slate-700 whitespace-pre-wrap max-h-[520px] overflow-auto"
                 data-testid="job-detail-jd-snapshot">
              {snapshot.jd_text}
            </div>
          ) : attachMode ? (
            <div className="space-y-3" data-testid="job-detail-manual-jd-form">
              <p className="text-xs text-slate-500">
                Automatic capture didn't work — paste the JD here. The snapshot is preserved even if the original job link expires.
              </p>
              <textarea rows={10} value={manualJd} onChange={(e) => setManualJd(e.target.value)}
                        data-testid="job-detail-manual-jd-input"
                        placeholder="Paste the job description (min 20 chars)"
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
              <div className="flex justify-end gap-2">
                <button onClick={() => { setAttachMode(false); setManualJd(""); }}
                        className="text-sm px-3 py-2 rounded border border-slate-200">Cancel</button>
                <button onClick={attachManual} disabled={busy}
                        data-testid="job-detail-manual-jd-submit"
                        className="text-sm px-4 py-2 rounded bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-50">
                  {busy ? "Saving…" : "Attach JD"}
                </button>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-amber-200 bg-amber-50 rounded-lg p-4 text-sm text-amber-800"
                 data-testid="job-detail-no-snapshot">
              <div className="font-medium flex items-center gap-2"><Warning size={14} />
                Job Description could not be captured automatically.
              </div>
              <p className="text-xs mt-1">
                Some career sites block automated capture. Attach the JD manually so it stays with this job record.
              </p>
              <button onClick={() => setAttachMode(true)}
                      data-testid="job-detail-attach-jd-btn"
                      className="mt-3 text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-amber-300 bg-white hover:bg-amber-100 text-amber-900">
                <PencilSimpleLine size={12} /> Attach JD manually
              </button>
            </div>
          )}
        </div>

        {/* Right — metadata & discovery */}
        <div className="space-y-4">
          <Panel title="Discovery">
            <MetaRow label="Originally posted"
                     value={job.originally_posted_at ? new Date(job.originally_posted_at).toLocaleString() : "Unknown"}
                     testid="job-detail-originally-posted" />
            <MetaRow label="Discovered by" value={job.discovered_by_name} testid="job-detail-discovered-by" />
            <MetaRow label="Discovered at" value={fmt(job.discovered_at)} />
            <MetaRow label="Source" value={job.source} testid="job-detail-source" />
            <MetaRow label="Source type" value={job.source_type} />
            {job.source_job_id && <MetaRow label="Source Job ID" value={job.source_job_id} />}
            <MetaRow label="Last verified" value={fmt(job.last_verified_at)} />
            <MetaRow label="Reuse count" value={String(job.reuse_count || 0)} />
          </Panel>

          <Panel title="Requirements">
            <MetaRow label="Skills (required)" value={(job.required_skills || []).join(", ") || "—"} />
            <MetaRow label="Skills (preferred)" value={(job.preferred_skills || []).join(", ") || "—"} />
            <MetaRow label="Experience" value={job.required_experience_years ? `${job.required_experience_years}+ years` : "—"} />
            <MetaRow label="Education" value={job.education} />
            <MetaRow label="Salary"
                     value={job.salary_min || job.salary_max
                        ? `${job.salary_min || "?"}${job.salary_max ? ` – ${job.salary_max}` : ""} ${job.salary_currency || ""}`
                        : "—"} />
          </Panel>

          <Panel title="JD fingerprint">
            <div className="text-xs text-slate-500 font-mono break-all" data-testid="job-detail-jd-hash">
              {job.jd_hash || "—"}
            </div>
          </Panel>
        </div>
      </div>

      {/* Phase 13 Beat 2 — Match Preview: pick a candidate and score in real time. */}
      <MatchPreviewPanel jobId={job.id} />
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function MetaRow({ label, value, testid }) {
  return (
    <div className="text-xs">
      <div className="text-slate-500">{label}</div>
      <div className="text-slate-800 mt-0.5" data-testid={testid}>{value || "—"}</div>
    </div>
  );
}
